// PainMapPicker — shared Profile pain picker (lifted from mobile 2026-08-16
// so web-rnw renders the real picker instead of a "do it on mobile" note).
// The BodyMap primitive it paints on already runs on RNW (mode="pick" +
// onMusclePress, lifted 2026-07-01); this component only had to shed its
// mobile-only persistence (ProfileContext + patchProfile) for an injected
// `savePainMap` capability, same shape as the other host-injected async
// capabilities in this package (see Logger/hostDeps.ts's saveNoteEvent etc).
//
// Gesture: tap a muscle to cycle severity none → red → yellow → none.
// Decision locked 2026-05-19 by Antenore (memory: feedback_pain_picker_tap_cycle).
// No longpress menu, no action sheet. Green was dropped 1.74.0 (an unmarked
// muscle is already safe by definition); tapping a legacy green entry from
// pre-1.74.0 data lands on `null` next (indexOf('green') = -1, +1 = 0) so old
// green markings still clear cleanly with one tap.
//
// Persistence: optimistic local state for snappy UI, then a debounced call
// (600ms after the last tap) to the injected `savePainMap`. The picker is the
// only surface that writes painMap; consumers read through the host's own
// profile snapshot.

import { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  PAIN_SEVERITY_COLOURS,
  paintFromSeverity,
  type BodyFigure,
  type MuscleSlug,
  type PainSeverity,
} from "@jackedlogs/shared";

import { ScaleLegend } from "../primitives/ScaleLegend";
import type { UiTheme } from "../theme";
import { BodyMap } from "./BodyMap";

const CYCLE: ReadonlyArray<PainSeverity | null> = [null, "red", "yellow"];

const nextSeverity = (
  current: PainSeverity | undefined,
): PainSeverity | null => {
  const idx = CYCLE.indexOf(current ?? null);
  return CYCLE[(idx + 1) % CYCLE.length] ?? null;
};

const SAVE_DEBOUNCE_MS = 600;

export type PainMapPickerProps = {
  theme: UiTheme;
  isDark?: boolean;
  initialPainMap: Partial<Record<MuscleSlug, PainSeverity>>;
  figure?: BodyFigure;
  savePainMap: (
    next: Partial<Record<MuscleSlug, PainSeverity>>,
  ) => Promise<void>;
};

export function PainMapPicker({
  theme,
  isDark,
  initialPainMap,
  figure,
  savePainMap,
}: PainMapPickerProps) {
  const { t } = useTranslation();

  // Local state seeds from the host snapshot on mount. After that, the
  // picker owns the state — echoing the host's own writes back in would
  // chase its own tail.
  const [severity, setSeverity] = useState<
    Partial<Record<MuscleSlug, PainSeverity>>
  >(() => ({ ...initialPainMap }));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef(severity);
  latestRef.current = severity;

  const persist = useCallback(
    async (next: Partial<Record<MuscleSlug, PainSeverity>>) => {
      setSaving(true);
      setSaveError(null);
      try {
        await savePainMap(next);
      } catch (err) {
        // Surface the real error so a 400 parse rejection reads differently
        // from a network blip — the picker is the only surface that writes
        // painMap, so a generic "Save failed" was too coarse.
        const e = err as { status?: number; body?: unknown; message?: string };
        const reason =
          typeof e?.status === "number"
            ? `${e.status} ${
                typeof e.body === "object" && e.body !== null
                  ? JSON.stringify(e.body).slice(0, 200)
                  : (e.message ?? "")
              }`
            : (e?.message ?? "network");
        setSaveError(`${t("bodymap.painSaveError")} (${reason})`);
      } finally {
        setSaving(false);
      }
    },
    [savePainMap, t],
  );

  const schedulePersist = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void persist(latestRef.current);
    }, SAVE_DEBOUNCE_MS);
  }, [persist]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  const onPress = (slug: MuscleSlug) => {
    const ns = nextSeverity(severity[slug]);
    setSeverity((prev) => {
      const out = { ...prev };
      if (ns === null) delete out[slug];
      else out[slug] = ns;
      return out;
    });
    schedulePersist();
  };

  const onClearAll = () => {
    if (Object.keys(severity).length === 0) return;
    setSeverity({});
    schedulePersist();
  };

  const value = paintFromSeverity(severity);
  const painted = Object.keys(severity).length;
  const styles = makeStyles(theme);

  return (
    <View style={styles.card}>
      <Text style={styles.label}>{t("bodymap.painTitle")}</Text>
      <Text style={styles.help}>{t("bodymap.painHelp")}</Text>

      <View style={styles.bodyWrap}>
        <BodyMap
          mode="pick"
          view="front"
          value={value}
          theme={theme}
          isDark={isDark}
          onMusclePress={onPress}
          scale={1.3}
          figure={figure}
          layout="dual"
        />
      </View>

      <ScaleLegend
        variant="dots"
        theme={theme}
        style={styles.legendRow}
        items={[
          { color: PAIN_SEVERITY_COLOURS.red, label: t("bodymap.painRed") },
          { color: PAIN_SEVERITY_COLOURS.yellow, label: t("bodymap.painYellow") },
        ]}
      />

      {saving ? (
        <Text style={styles.statusSaving}>{t("bodymap.painSaving")}</Text>
      ) : null}
      {saveError ? (
        <Text style={styles.statusError}>{saveError}</Text>
      ) : null}

      {painted > 0 ? (
        <Pressable
          accessibilityRole="button"
          onPress={onClearAll}
          style={({ pressed }) => [
            styles.clearBtn,
            pressed && styles.clearBtnPressed,
          ]}
        >
          <Text style={styles.clearBtnText}>{t("bodymap.painClearAll")}</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    label: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "600",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    help: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
    },
    bodyWrap: {
      alignItems: "center",
      paddingVertical: s.s2,
    },
    legendRow: {
      flexDirection: "row",
      justifyContent: "center",
      gap: s.s5,
      paddingVertical: s.s2,
    },
    statusSaving: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontStyle: "italic",
    },
    statusError: {
      color: p.danger,
      fontSize: ty.sizes.sm,
    },
    clearBtn: {
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingVertical: s.s4,
      alignItems: "center",
      minHeight: 44,
      justifyContent: "center",
    },
    clearBtnPressed: {
      backgroundColor: p.surface2,
    },
    clearBtnText: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "600",
    },
  });
}
